const REFERRAL_STORAGE_KEY = 'via'
const REWARDFUL_READY_TIMEOUT_MS = 1500

const getStorage = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return window.sessionStorage || null
}

const normalizeReferral = (value) => {
  if (typeof value !== 'string') {
    return ''
  }

  return value.trim()
}

const getRewardful = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return window.Rewardful || null
}

const getRewardfulSource = () => {
  const rewardful = getRewardful()

  return normalizeReferral(
    rewardful?.source ||
      rewardful?.affiliate ||
      rewardful?.affiliateId ||
      rewardful?.promoterAffiliateId
  )
}

const getRewardfulReferral = () =>
  normalizeReferral(getRewardful()?.referral)

const waitForRewardfulReady = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve()
      return
    }

    if (getRewardfulReferral()) {
      resolve()
      return
    }

    if (typeof window.rewardful !== 'function') {
      resolve()
      return
    }

    let settled = false
    const finish = () => {
      if (!settled) {
        settled = true
        resolve()
      }
    }

    const timeoutId = window.setTimeout(finish, REWARDFUL_READY_TIMEOUT_MS)

    try {
      window.rewardful('ready', () => {
        window.clearTimeout(timeoutId)
        finish()
      })
    } catch (error) {
      window.clearTimeout(timeoutId)
      finish()
    }
  })

export const captureReferralFromUrl = () => {
  if (typeof window === 'undefined') {
    return null
  }

  let via = ''

  try {
    via = normalizeReferral(
      new URLSearchParams(window.location.search).get('via')
    )
  } catch (error) {
    return null
  }

  if (!via) {
    return null
  }

  try {
    getStorage()?.setItem(REFERRAL_STORAGE_KEY, via)
  } catch (error) {
    return null
  }

  return via
}

export const getStoredReferral = () => {
  try {
    return normalizeReferral(getStorage()?.getItem(REFERRAL_STORAGE_KEY))
  } catch (error) {
    return ''
  }
}

export const getPromoterAffiliateId = () =>
  getStoredReferral() || getRewardfulSource()

export const getRewardfulAttribution = async () => {
  await waitForRewardfulReady()

  const rewardfulReferral = getRewardfulReferral()
  const promoterAffiliateId = getPromoterAffiliateId()

  return {
    ...(rewardfulReferral && {
      rewardfulReferral,
      referral: rewardfulReferral,
    }),
    ...(promoterAffiliateId && {
      promoterAffiliateId,
      via: promoterAffiliateId,
    }),
  }
}

export const buildPayloadWithRewardfulAttribution = async (payload) => {
  const attribution = await getRewardfulAttribution()

  if (!Object.keys(attribution).length) {
    return payload
  }

  return {
    ...payload,
    ...attribution,
  }
}

export const buildSignupPayloadWithReferral = (payload) => {
  const via = getPromoterAffiliateId()

  if (!via) {
    return payload
  }

  return {
    ...payload,
    promoterAffiliateId: via,
    via,
  }
}

export const buildReferralStripeCustomerPayload = ({
  attribution = {},
  userId,
  email,
  firstName,
  lastName,
  phone,
}) => {
  if (!Object.keys(attribution).length || !userId) {
    return null
  }

  const referral = attribution?.rewardfulReferral || attribution?.referral
  const promoterAffiliateId =
    attribution?.promoterAffiliateId || attribution?.via

  return {
    userId,
    email,
    name: `${firstName || ''} ${lastName || ''}`.trim(),
    ...(phone && {phone}),
    ...(referral && {referral}),
    ...(promoterAffiliateId && {promoterAffiliateId}),
  }
}
